import React, {useState, useContext, useRef, useEffect} from "react";
import StyleContext from "../../../contexts/StyleContext";
import "./ArenaLabSlideshow.css";

// Automatically import all images from the events folder
const context = require.context('../../../assets/images/events', false, /\.(png|jpe?g|svg|gif)$/);
let imageFilenames = context.keys().map(key => key.replace('./', ''));
let eventImages = context.keys().map(context);

// Create image objects with filename and image paired together
let imageData = imageFilenames.map((filename, index) => ({
  filename,
  image: eventImages[index]
}));

// Shuffle function
const shuffle = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Shuffle image data to keep filename and image together
imageData = shuffle(imageData);
eventImages = imageData.map(item => item.image);
imageFilenames = imageData.map(item => item.filename);

// Create infinite loop array
const infiniteImages = [...eventImages, ...eventImages, ...eventImages];
const infiniteFilenames = [...imageFilenames, ...imageFilenames, ...imageFilenames];

// IMAGE DESCRIPTIONS - Short objective descriptions
const IMAGE_DESCRIPTIONS = {
  'bestbuddynusopenhouseday.jpeg': 'Best Buddies @ NUS Open House Day',
  'amazonevent.jpeg': 'Professors Gathering @ Amazon Networking Dinner',
  'demodaywithpresident.jpeg': 'Demo Day with President',
  'favcafeatnus.jpeg': 'Our Favorite NUS Café',
  'friendsnus.jpeg': 'Friends at NUS',
  'fullhousessi.jpeg': 'Full House at SSI',
  'goodfriends.jpeg': 'Good Friends Moment',
  'haroldstenurecelebration.jpeg': 'Harold\'s Tenure Celebration',
  'haroldstenurecelebration2.jpeg': 'Harold\'s Tenure Celebration',
  'haroldtenure.jpeg': 'Harold\'s Tenure Party',
  'havingfundaticra24.jpeg': 'ICRA 2024 memories',
  'icra24friendsanddad.jpeg': 'ICRA 2024 with Friends and Dad',
  'icra24haroldspeech.jpeg': 'ICRA 2024 Harold\'s Speech',
  'icra24yokohama.jpeg': 'ICRA 2024 Yokohama',
  'icra25allanwang.jpeg': 'ICRA 2025 Allan Presentation',
  'icra25atlanta.jpeg': 'ICRA 2025 Atlanta SocNav Dinner',
  'icra25vovapresentation.jpeg': 'ICRA 2025 Vova\'s Presentation',
  'icra25workshop.jpeg': 'ICRA 2025 Workshop',
  'icra26haroldspeech.jpeg': 'ICRA 2026 Harold\'s Speech',
  'nusclearlabjiaming.jpeg': 'NUS CLEAR Lab with Jiaming',
  'nusclearlabjiaming2.jpeg': 'NUS CLEAR Lab ',
  'openssidaynus.jpeg': 'NUS Open SSI Day',
  'openssidaynusharoldphotobom.jpeg': 'Open SSI Day Photo Bomb by Harold',
  'openssidaypreparations.jpeg': 'Open SSI Day Preparations',
  'presentingatrobosg.jpeg': 'Presenting at RoboSG',
  'robosg.jpeg': 'RoboSG Event',
  'robosgdemo.jpeg': 'RoboSG Demo 1',
  'robosgdemo2.jpeg': 'RoboSG Demo 2',
  'robosgdemonstration.jpeg': 'RoboSG Demonstration',
  'rss24group.jpeg': 'RSS 2024 Delft Memories',
  'rss24grouppic.jpeg': 'RSS 2024 Delft Memories',
  'rss25speech.jpeg': 'RSS 2025 Speech',
  'rss25withvovaandfriends.jpeg': 'RSS 2025 with Vova and Friends',
  'ssidemoday.jpeg': 'SSI Demo Day',
  'tubteam.jpeg': 'TUB Team hangout',
  'tubteam2.jpeg': 'TUB Team night out',
  'tubteam3.jpeg': 'TUB Team celebration',
  'withgoodfriendsofNUS.jpeg': 'With Good Friends at NUS',
  'withsamsonbuddy.jpeg': 'With Samson - Team Buddy',
  'workshoporganizersicra25.jpeg': 'ICRA 2025 Workshop Organizers',
};

function ArenaLabSlideshow() {
  const {isDark} = useContext(StyleContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(eventImages.length);
  const scrollRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const itemWidth = 500 + 32; // image width + gap
      const scrollLeft = scrollContainer.scrollLeft;
      const scrollThreshold = itemWidth * eventImages.length;
      
      // If scrolled to the end of first set, jump to middle
      if (scrollLeft > scrollThreshold * 1.5) {
        scrollContainer.scrollLeft = scrollThreshold * 0.5;
      }
      // If scrolled to the beginning, jump to middle
      else if (scrollLeft < scrollThreshold * 0.3) {
        scrollContainer.scrollLeft = scrollThreshold * 1;
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const handleImageClick = (index) => {
    // Get the actual image index (loop back to original array)
    const actualIndex = index % eventImages.length;
    setCurrentIndex(actualIndex);
    setModalOpen(true);
  };

  const handleNextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % eventImages.length);
  };

  const handlePrevImage = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? eventImages.length - 1 : prev - 1
    );
  };

  const handleCloseModal = (e) => {
    if (e.target.className === 'events-modal-overlay') {
      setModalOpen(false);
    }
  };

  const currentFilename = imageFilenames[currentIndex];
  const currentDescription = IMAGE_DESCRIPTIONS[currentFilename] || currentFilename;

  return (
    <>
      <div className="events-gallery-container">
        <div className={`events-gallery ${isDark ? "dark-mode" : ""}`}>
          <div className="events-scroll-wrapper" ref={scrollRef}>
            {infiniteImages.map((image, index) => (
              <div 
                key={index} 
                className="event-image-wrapper"
                onClick={() => handleImageClick(index)}
              >
                <img 
                  src={image} 
                  alt={`Event ${index + 1}`} 
                  className="event-image"
                />
                <div className="event-image-overlay">
                  <span className="click-to-expand">Click to expand</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal for enlarged image view */}
      {modalOpen && (
        <div className="events-modal-overlay" onClick={handleCloseModal}>
          <div className="events-modal">
            <button className="modal-close-btn" onClick={() => setModalOpen(false)}>✕</button>
            
            <div className="modal-content">
              <button className="modal-nav-btn prev" onClick={handlePrevImage}>❮</button>
              
              <div className="modal-image-container">
                <img 
                  src={eventImages[currentIndex]} 
                  alt={`Event ${currentIndex + 1}`}
                  className="modal-image"
                />
              </div>
              
              <button className="modal-nav-btn next" onClick={handleNextImage}>❯</button>
            </div>

            <div className="modal-description">
              <p>{currentDescription}</p>
              <div className="modal-counter">
                {currentIndex + 1} / {eventImages.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ArenaLabSlideshow;
